import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Calendar, Phone, Video, Building2, CheckCircle } from 'lucide-react';
import apiClient from '@/utils/api';
import { toast } from 'sonner';

export const ConsultationModal = ({ isOpen, onClose, quoteId, formData }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookingId, setBookingId] = useState('');
  
  const [bookingData, setBookingData] = useState({
    name: formData?.name || '',
    email: formData?.email || '',
    phone: formData?.phone || '',
    preferred_date: '',
    preferred_time: 'morning',
    consultation_type: 'site_visit',
    address: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingData.name || !bookingData.email || !bookingData.phone) {
      toast.error('Please fill in your contact details');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/interior-design/book-consultation', {
        ...bookingData,
        quote_id: quoteId
      });
      
      setBookingId(response.data.booking_id);
      setSuccess(true);
      toast.success('Consultation booked successfully!');
    } catch (error) {
      toast.error('Failed to book consultation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-green-600 p-6 rounded-t-2xl text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Book a Consultation</h2>
              <p className="text-white/80 text-sm mt-1">Our design team will reach out within 24 hours</p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold mb-2">Booking Confirmed!</h3>
            <p className="text-gray-600 mb-4">
              Your consultation has been scheduled. Our team will contact you at{' '}
              <strong>{bookingData.phone}</strong> within 24 hours.
            </p>
            <p className="text-sm text-gray-500 mb-6">Booking ID: {bookingId}</p>
            <Button onClick={onClose} className="w-full">
              Done
            </Button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Contact Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={bookingData.name}
                  onChange={(e) => setBookingData({...bookingData, name: e.target.value})}
                  placeholder="Your name"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  type="tel"
                  value={bookingData.phone}
                  onChange={(e) => setBookingData({...bookingData, phone: e.target.value})}
                  placeholder="10-digit mobile"
                  className="mt-1"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={bookingData.email}
                onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                placeholder="your@email.com"
                className="mt-1"
                required
              />
            </div>

            {/* Consultation Type */}
            <div>
              <Label className="mb-2 block">Consultation Type</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'site_visit', label: 'Site Visit', icon: Building2 },
                  { value: 'video_call', label: 'Video Call', icon: Video },
                  { value: 'office_visit', label: 'Office Visit', icon: Calendar }
                ].map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setBookingData({...bookingData, consultation_type: type.value})}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      bookingData.consultation_type === type.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <type.icon className={`w-5 h-5 mx-auto mb-1 ${
                      bookingData.consultation_type === type.value ? 'text-primary' : 'text-gray-400'
                    }`} />
                    <span className="text-xs font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Preferred Date</Label>
                <Input
                  type="date"
                  value={bookingData.preferred_date}
                  onChange={(e) => setBookingData({...bookingData, preferred_date: e.target.value})}
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Preferred Time</Label>
                <Select 
                  value={bookingData.preferred_time}
                  onValueChange={(v) => setBookingData({...bookingData, preferred_time: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning (9 AM - 12 PM)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (12 PM - 4 PM)</SelectItem>
                    <SelectItem value="evening">Evening (4 PM - 7 PM)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address (for site visit) */}
            {bookingData.consultation_type === 'site_visit' && (
              <div>
                <Label>Site Address</Label>
                <Textarea
                  value={bookingData.address}
                  onChange={(e) => setBookingData({...bookingData, address: e.target.value})}
                  placeholder="Enter your property address"
                  className="mt-1"
                  rows={2}
                />
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>Additional Notes (Optional)</Label>
              <Textarea
                value={bookingData.notes}
                onChange={(e) => setBookingData({...bookingData, notes: e.target.value})}
                placeholder="Any specific requirements or questions..."
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Submit */}
            <Button type="submit" className="w-full h-12" disabled={loading}>
              {loading ? 'Booking...' : 'Book Consultation'}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              By booking, you agree to receive calls and messages regarding your consultation.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ConsultationModal;
